import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { sanitizeForDisplay } from '../../utils/htmlSanitizer';
import { getTokenForRequest } from '../../utils/apiAuth';

const FILE_SERVER_URL =
  (typeof window !== 'undefined' && window.fileServerUrl) ||
  'https://d6adgef862n1y.cloudfront.net/';

const CAMTASIA_URL =
  (typeof window !== 'undefined' && window.camtasiaURL) || 'Camtasia.aspx';

/** Above .student-review-modal (99999) and nested overlays (100001) */
const PREVIEW_MODAL_Z_INDEX = 100050;

/**
 * Build a CloudFront URL from a file table Path or full URL (same as socrates-question.js).
 */
const buildMediaUrl = (filePath) => {
  if (!filePath) return null;
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath;
  return FILE_SERVER_URL + filePath;
};

const normalizeMediaType = (type) => String(type || '').toLowerCase();

/**
 * Helper function to encode token for API calls
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * ReviewQuestionDisplay Component
 * Displays a review question with student's answer highlighted
 */
const ReviewQuestionDisplay = ({ reviewQuestion, token, onNextQuestion, canGoNext, isLastQuestion, isCorrect: isCorrectFromParent }) => {
  const [questionData, setQuestionData] = useState(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const solutionRef = useRef(null);

  if (!reviewQuestion || !reviewQuestion.Question) {
    return (
      <div className="alert alert-warning" role="alert">
        Question data not available.
      </div>
    );
  }

  const questionId = reviewQuestion.QuestionID;
  const myChoices = reviewQuestion.MyChoices || [];
  const myAnswers = reviewQuestion.MyAnswers || []; // Array for FBQ
  const myAnswer = reviewQuestion.MyAnswer || ''; // Fallback for backward compatibility

  // Fetch full question data with Choices and IsCorrect flags
  useEffect(() => {
    const fetchQuestionData = async () => {
      if (!questionId) return;
      
      try {
        setIsLoadingQuestion(true);
        const response = await axios({
          url: `/webservices/Question.svc/Basic?token=${encodeToken(token)}&questionId=${questionId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json; charset=utf-8'
          }
        });

        if (response.data) {
          setQuestionData(response.data);
        }
      } catch (error) {
        console.error('Error fetching question data:', error);
      } finally {
        setIsLoadingQuestion(false);
      }
    };

    fetchQuestionData();
  }, [questionId]);

  // Helper function to render MathJax for a container
  const renderMathJaxForContainer = (container) => {
    if (!container || !document.contains(container)) return;

    // Find all math elements in the container
    const mathElements1 = container.querySelectorAll('.mathformula');
    const mathElements2 = container.querySelectorAll('[data-tex]');
    const mathElements3 = container.querySelectorAll('span[class*="math"]');
    const allMathElements = [
      ...Array.from(mathElements1),
      ...Array.from(mathElements2),
      ...Array.from(mathElements3)
    ];
    const validElements = Array.from(allMathElements).filter(el => el && document.contains(el));

    if (typeof window.MathJax !== 'undefined' && window.MathJax.typesetPromise) {
      try {
        // Always typeset the entire container to ensure all content (question, solution, hints) is processed
        if (container && document.contains(container)) {
          window.MathJax.typesetPromise([container]).then(() => {
            // Success
          }).catch((error) => {
            // Silently handle errors
          });
        } else if (validElements.length > 0) {
          // Fallback: typeset specific elements if container is not valid
          window.MathJax.typesetPromise(validElements).then(() => {
            // Success
          }).catch((error) => {
            // Silently handle errors
          });
        }
      } catch (error) {
        // Silently handle errors
      }
    } else if (typeof window.typesetMathJax === 'function') {
      try {
        // Use helper function - it will handle the container
        if (container && document.contains(container)) {
          window.typesetMathJax(container);
        } else if (validElements.length > 0) {
          window.typesetMathJax(validElements);
        }
      } catch (error) {
        // Silently handle errors
      }
    }
  };

  // Always render MathJax when question data is loaded or solution visibility changes
  useEffect(() => {
    if (!questionData) return;

    const renderMathJax = () => {
      // Find the review question display container
      const container = document.querySelector('.review-question-display');
      if (container) {
        renderMathJaxForContainer(container);
      }
    };

    // Wait for DOM to update, then render MathJax
    // Use multiple timeouts to catch content that loads asynchronously (including solution when shown)
    const timer1 = setTimeout(renderMathJax, 100);
    const timer2 = setTimeout(renderMathJax, 300);
    const timer3 = setTimeout(renderMathJax, 500);
    const timer4 = setTimeout(renderMathJax, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [questionData, showSolution]);

  if (isLoadingQuestion || !questionData) {
    return (
      <div className="text-center" style={{ padding: '20px' }}>
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const question = questionData;
  const questionType = question.QuestionType;

  // Get correct choice IDs
  const correctChoiceIds = (question.Choices || [])
    .filter(choice => choice.IsCorrect)
    .map(choice => choice.ID);

  const renderMediaFile = (file, options = {}) => {
    if (!file || !file.FilePath) return null;

    const { alt = 'Attachment', marginTop = 0, marginBottom = 8 } = options;
    const fileType = normalizeMediaType(file.Type);
    const fileUrl = buildMediaUrl(file.FilePath);
    const wrapperStyle = { marginTop, marginBottom };

    if (fileType === 'image') {
      return (
        <div style={wrapperStyle}>
          <img
            src={fileUrl}
            alt={alt}
            style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
          />
        </div>
      );
    }

    if (fileType === 'video') {
      return (
        <div style={wrapperStyle}>
          <video
            src={fileUrl}
            controls
            controlsList="nodownload"
            preload="metadata"
            style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px', backgroundColor: '#000' }}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    if (fileType === 'audio') {
      return (
        <div style={wrapperStyle}>
          <audio
            src={fileUrl}
            controls
            controlsList="nodownload"
            preload="metadata"
            style={{ width: '100%' }}
          >
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    return null;
  };

  const isCamtasiaScaffold = (scaffold) =>
    (scaffold.CamtasiaID && scaffold.CamtasiaID > 0) ||
    (scaffold.CamtasiaName && scaffold.CamtasiaName.length > 0);

  const openScaffoldMedia = (scaffold) => {
    if (isCamtasiaScaffold(scaffold)) {
      window.open(`${CAMTASIA_URL}?camtasiaID=${scaffold.CamtasiaID}`, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!scaffold.FilePath || !scaffold.FileType) return;

    // Use React preview (portaled above student-review-modal). Legacy previewFile uses
    // .modalcontainer/Bootstrap z-index and renders behind the review modal.
    setPreviewMedia({
      filePath: scaffold.FilePath,
      fileType: scaffold.FileType,
      title: scaffold.CamtasiaName || scaffold.Desc || 'View Media'
    });
  };

  const getScaffoldHintLabel = (scaffold) => {
    if (isCamtasiaScaffold(scaffold)) return 'Video Hint';
    const ft = normalizeMediaType(scaffold.FileType);
    if (ft === 'video') return 'Video Hint';
    if (ft === 'image') return 'Image Hint';
    if (ft === 'audio') return 'Audio Hint';
    return 'Text Hint';
  };

  const renderScaffoldMediaControls = (scaffold, buttonStyle = {}) => {
    const hasFileMedia = scaffold.FilePath && scaffold.FileType;
    const showCamtasia = isCamtasiaScaffold(scaffold);
    const showFilePreview = hasFileMedia && !showCamtasia;

    if (!showCamtasia && !showFilePreview) return null;

    const label = showCamtasia
      ? 'Play Video'
      : normalizeMediaType(scaffold.FileType) === 'image'
        ? 'View Image'
        : normalizeMediaType(scaffold.FileType) === 'audio'
          ? 'Play Audio'
          : 'Play Video';

    return (
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={() => openScaffoldMedia(scaffold)}
        style={buttonStyle}
      >
        {label}
      </button>
    );
  };

  const renderScaffoldBlock = (scaffold, options = {}) => {
    const { buttonStyle = {}, showInlineVideo = false } = options;
    const ft = normalizeMediaType(scaffold.FileType);
    const hasFileMedia = scaffold.FilePath && scaffold.FileType;
    const showCamtasia = isCamtasiaScaffold(scaffold);

    return (
      <div>
        {scaffold.CamtasiaName && (
          <div style={{ marginBottom: '5px', fontSize: '12px', fontWeight: '500' }}>
            {scaffold.CamtasiaName}
          </div>
        )}
        {scaffold.Desc && (
          <div
            style={{ fontSize: '11px', color: '#333', marginBottom: '8px', lineHeight: '1.4' }}
            dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(scaffold.Desc) }}
          />
        )}
        {showInlineVideo && hasFileMedia && !showCamtasia && ft === 'video' && (
          <div style={{ marginBottom: '8px' }}>
            <video
              src={buildMediaUrl(scaffold.FilePath)}
              controls
              controlsList="nodownload"
              preload="metadata"
              style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px', backgroundColor: '#000' }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )}
        {showInlineVideo && hasFileMedia && !showCamtasia && ft === 'image' && (
          <div style={{ marginBottom: '8px' }}>
            <img
              src={buildMediaUrl(scaffold.FilePath)}
              alt="Hint"
              style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
            />
          </div>
        )}
        {showInlineVideo && hasFileMedia && !showCamtasia && ft === 'audio' && (
          <div style={{ marginBottom: '8px' }}>
            <audio
              src={buildMediaUrl(scaffold.FilePath)}
              controls
              controlsList="nodownload"
              preload="metadata"
              style={{ width: '100%' }}
            >
              Your browser does not support the audio tag.
            </audio>
          </div>
        )}
        {renderScaffoldMediaControls(scaffold, buttonStyle)}
      </div>
    );
  };

  const renderPreviewModal = () => {
    if (!previewMedia) return null;

    const fileUrl = buildMediaUrl(previewMedia.filePath);
    const fileType = normalizeMediaType(previewMedia.fileType);

    const modal = (
      <div
        className="review-media-preview-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          zIndex: PREVIEW_MODAL_Z_INDEX,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={() => setPreviewMedia(null)}
      >
        <div
          className="review-media-preview-dialog"
          style={{
            position: 'relative',
            zIndex: PREVIEW_MODAL_Z_INDEX + 1,
            backgroundColor: '#fff',
            borderRadius: '8px',
            maxWidth: '880px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '16px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h5 style={{ margin: 0 }}>View Media</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setPreviewMedia(null)} />
          </div>
          {fileType === 'image' && (
            <img src={fileUrl} alt="Preview" style={{ maxWidth: '100%', height: 'auto' }} />
          )}
          {fileType === 'video' && (
            <video
              src={fileUrl}
              controls
              controlsList="nodownload"
              preload="metadata"
              style={{ width: '100%', maxHeight: '70vh' }}
            >
              Your browser does not support the video tag.
            </video>
          )}
          {fileType === 'audio' && (
            <audio src={fileUrl} controls controlsList="nodownload" preload="metadata" style={{ width: '100%' }}>
              Your browser does not support the audio tag.
            </audio>
          )}
          {!['image', 'video', 'audio'].includes(fileType) && (
            <p className="text-muted mb-0">Preview is not available for this file type.</p>
          )}
        </div>
      </div>
    );

    return createPortal(modal, document.body);
  };

  // Render choice with highlighting
  const renderChoice = (choice, index) => {
    const isStudentChoice = myChoices.includes(choice.ID);
    const isCorrectChoice = choice.IsCorrect;
    
    // Determine background color
    let backgroundColor = 'white';
    let borderColor = '#ddd';
    
    if (isStudentChoice && isCorrectChoice) {
      // Student selected correct answer - green
      backgroundColor = '#d4edda';
      borderColor = '#28a745';
    } else if (isStudentChoice && !isCorrectChoice) {
      // Student selected wrong answer - red
      backgroundColor = '#f8d7da';
      borderColor = '#dc3545';
    } else if (!isStudentChoice && isCorrectChoice) {
      // Correct answer not selected by student - green
      backgroundColor = '#d4edda';
      borderColor = '#28a745';
    }

    return (
      <div 
        key={choice.ID || index}
        style={{
          padding: '6px 8px',
          marginBottom: '6px',
          backgroundColor: backgroundColor,
          border: `2px solid ${borderColor}`,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'flex-start'
        }}
      >
        <span style={{ fontWeight: 'bold', marginRight: '10px', minWidth: '20px' }}>
          {String.fromCharCode(65 + index)}.
        </span>
        <div style={{ flex: 1 }}>
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(choice.Desc || choice.Description || '') }}
          />
          {renderMediaFile(choice.AdditionalFile, { alt: 'Choice attachment', marginTop: 6, marginBottom: 0 })}
        </div>
      </div>
    );
  };

  // Helper function to compare a single student answer with a correct answer
  const compareFBQAnswer = (studentText, answer) => {
      const studentNum = Number(studentText);
      const isStudentNumeric = !isNaN(studentNum) && studentText !== '';
      
      // If MatchMode is null, use NumRangeMin/Max for comparison
      if (answer.MatchMode === null || answer.MatchMode === undefined) {
        if (answer.NumRangeMin !== undefined && answer.NumRangeMax !== undefined) {
          if (isStudentNumeric) {
            const minNum = Number(answer.NumRangeMin);
            const maxNum = Number(answer.NumRangeMax);
            return studentNum >= minNum && studentNum <= maxNum;
          }
          return false;
        }
        // Fallback: if no range but TextAnswer exists, compare as text
        if (answer.TextAnswer) {
          const correctText = String(answer.TextAnswer || '').trim();
          if (correctText === '') return false;
          
          // If both are numeric, compare as numbers
          const correctNum = Number(correctText);
          const isCorrectNumeric = !isNaN(correctNum) && correctText !== '';
          
          if (isStudentNumeric && isCorrectNumeric) {
            return studentNum === correctNum;
          }
          
          // For text answers, check case sensitivity
          if (answer.CaseSensitive) {
            return studentText === correctText;
          } else {
            return studentText.toLowerCase() === correctText.toLowerCase();
          }
        }
        return false;
      }
      
      // MatchMode is "Exact Match" or "Partial Match" - use TextAnswer
      const correctText = String(answer.TextAnswer || '').trim();
      if (correctText === '') return false;
      
      // Determine comparison method based on MatchMode
      if (answer.MatchMode === 'Exact Match' || answer.MatchMode === 'Exact') {
        // Exact match - respect case sensitivity
        if (answer.CaseSensitive) {
          return studentText === correctText;
        } else {
          return studentText.toLowerCase() === correctText.toLowerCase();
        }
      } else if (answer.MatchMode === 'Partial Match' || answer.MatchMode === 'Partial') {
        // Partial match - check if student answer contains or is contained in correct answer
        if (answer.CaseSensitive) {
          return studentText.includes(correctText) || correctText.includes(studentText);
        } else {
          const studentLower = studentText.toLowerCase();
          const correctLower = correctText.toLowerCase();
          return studentLower.includes(correctLower) || correctLower.includes(studentLower);
        }
      } else {
        // Unknown MatchMode - fallback to exact match
        if (answer.CaseSensitive) {
          return studentText === correctText;
        } else {
          return studentText.toLowerCase() === correctText.toLowerCase();
        }
      }
  };

  // Render FBQ answer
  const renderFBQAnswer = () => {
    // Get correct answers from Answers array (from Basic API response)
    const correctAnswers = question.Answers || [];
    // Get all student answers - for multi-blank questions, show all answers
    const studentAnswersToDisplay = myAnswers.length > 0 ? myAnswers : (myAnswer ? [myAnswer] : []);
    const studentAnswerDisplay = studentAnswersToDisplay.map(ans => String(ans || '')).filter(ans => ans !== '').join(', ') || 'No answer provided';
    // For comparison, use first answer for single-blank, or all for multi-blank
    const studentAnswerForComparison = studentAnswersToDisplay.length > 0 ? String(studentAnswersToDisplay[0] || '') : String(myAnswer || '');
    
    // Check if student answer matches any correct answer
    let isCorrect = false;
    let correctAnswerText = '';
    
    // Priority 1: Use correctness from parent component (from QuestionCorrectData)
    if (isCorrectFromParent !== undefined && isCorrectFromParent !== null) {
      isCorrect = isCorrectFromParent === true;
    }
    // Priority 2: Check if reviewQuestion has IsCorrect property explicitly set (from API)
    else if (reviewQuestion.IsCorrect !== undefined && reviewQuestion.IsCorrect !== null) {
      isCorrect = reviewQuestion.IsCorrect === true || reviewQuestion.IsCorrect === 1 || reviewQuestion.IsCorrect === '1';
    }
    
    if (correctAnswers.length > 0) {
      // Build correct answer text display - show all answers in sequence
      const answerTexts = correctAnswers.map((ans, index) => {
        // Priority 1: Check for numeric range (when MatchMode is null or undefined)
        if (ans.NumRangeMin !== undefined && ans.NumRangeMax !== undefined && ans.NumRangeMin !== null && ans.NumRangeMax !== null) {
          if (ans.NumRangeMin === ans.NumRangeMax) {
            return String(ans.NumRangeMin);
          } else {
            return `${ans.NumRangeMin} ~ ${ans.NumRangeMax}`;
          }
        }
        // Priority 2: Check for text answer (when MatchMode is "Exact Match" or "Partial Match")
        if (ans.TextAnswer !== null && ans.TextAnswer !== undefined && ans.TextAnswer !== '') {
          return String(ans.TextAnswer);
        }
        return null;
      }).filter(text => text !== null && text !== '');
      
      correctAnswerText = answerTexts.join(', ');
      
      // Priority 3: If correctness was not explicitly set, calculate it by comparing answers
      if (isCorrectFromParent === undefined && (reviewQuestion.IsCorrect === undefined || reviewQuestion.IsCorrect === null)) {
        // For multi-answer FBQ, check each answer in sequence
        if (correctAnswers.length > 1 && myAnswers.length > 1) {
          // All answers must match for multi-answer questions
          isCorrect = correctAnswers.every((answer, index) => {
            const studentAnswerForBlank = String((myAnswers[index] || '') || '').trim();
            return compareFBQAnswer(studentAnswerForBlank, answer);
          });
        } else {
          // Single answer or fallback to first answer
          isCorrect = correctAnswers.some(answer => {
            return compareFBQAnswer(studentAnswerForComparison.trim(), answer);
          });
        }
      }
    }
    
    return (
      <div style={{ marginTop: '15px' }}>
        <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Student's Answer:</div>
        <div 
          style={{
            padding: '10px',
            marginBottom: '10px',
            backgroundColor: isCorrect ? '#d4edda' : '#f8d7da',
            border: `2px solid ${isCorrect ? '#28a745' : '#dc3545'}`,
            borderRadius: '4px'
          }}
        >
          {studentAnswerDisplay}
        </div>
        
        {!isCorrect && correctAnswerText && (
          <>
            <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#28a745' }}>Correct Answer:</div>
            <div 
              style={{
                padding: '10px',
                backgroundColor: '#d4edda',
                border: '2px solid #28a745',
                borderRadius: '4px'
              }}
            >
              {correctAnswerText}
            </div>
          </>
        )}
      </div>
    );
  };

  // Get hints/scaffolds (filter out solution videos)
  const hints = (question.Scaffolds || []).filter(scaffold => scaffold.VideoType !== 'Q');

  // Get solution/scaffolds with VideoType === 'Q'
  const solutions = (question.Scaffolds || []).filter(scaffold => scaffold.VideoType === 'Q');

  return (
    <div className="review-question-display" style={{ padding: '5px', display: 'flex', gap: '12px', height: '100%', minHeight: 0, backgroundColor: '#fff' }}>
      {/* Main Question Content */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', backgroundColor: '#fff' }}>
        {/* Question Statement */}
        <div 
          style={{ marginBottom: '10px', fontSize: '14px', lineHeight: '1.5' }}
          dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(question.Statement || '') }}
        />

        {/* Question statement media (StatementFile) */}
        {renderMediaFile(question.StatementFile, { alt: 'Question', marginBottom: 8 })}

        {/* Choices for MCQ/MRQ */}
        {(questionType === 'MCQ' || questionType === 'MRQ') && question.Choices && (
          <div style={{ marginTop: '8px' }}>
            {question.Choices.map((choice, index) => renderChoice(choice, index))}
          </div>
        )}

        {/* Answer for FBQ */}
        {questionType === 'FBQ' && renderFBQAnswer()}

        {/* Solution Section */}
        {showSolution && (solutions.length > 0 || question.Explanation || question.ExplanationFile) && (
          <div ref={solutionRef} style={{ marginTop: '15px', padding: '12px', backgroundColor: '#f0f8ff', border: '2px solid #007bff', borderRadius: '6px' }}>
            <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '14px', color: '#007bff' }}>
              Solution
            </div>
            
            {/* Show explanation if available */}
            {question.Explanation && (
              <div 
                style={{
                  fontSize: '12px',
                  color: '#333',
                  lineHeight: '1.5',
                  marginBottom: question.ExplanationFile || solutions.length > 0 ? '15px' : '0'
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(question.Explanation) }}
              />
            )}

            {renderMediaFile(question.ExplanationFile, {
              alt: 'Explanation',
              marginBottom: solutions.length > 0 ? 15 : 0
            })}

            {/* Solution scaffolds */}
            {solutions.map((solution, index) => (
              <div key={solution.ID || index} style={{ marginBottom: index < solutions.length - 1 ? '12px' : '0' }}>
                {isCamtasiaScaffold(solution) || (solution.FilePath && solution.FileType) ? (
                  renderScaffoldBlock(solution, {
                    buttonStyle: {
                      display: 'inline-block',
                      padding: '6px 12px',
                      backgroundColor: '#007bff',
                      color: '#fff',
                      borderRadius: '4px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    },
                    showInlineVideo: true
                  })
                ) : (
                  <div
                    style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(solution.Desc || '') }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hints Section - Always show */}
      <div style={{ width: '280px', flexShrink: 0, borderLeft: '2px solid #dee2e6', paddingLeft: '12px', backgroundColor: '#fff' }}>
        {/* Action Buttons */}
        <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onNextQuestion}
            disabled={!canGoNext}
            style={{ 
              padding: '6px 12px', 
              fontSize: '12px', 
              width: '100%',
              opacity: canGoNext ? 1 : 0.6,
              cursor: canGoNext ? 'pointer' : 'not-allowed'
            }}
          >
            {isLastQuestion ? 'Finish' : 'Next Question'}
          </button>
          <button
            type="button"
            className="btn btn-warning"
            onClick={() => setShowSolution(!showSolution)}
            style={{ 
              padding: '6px 12px', 
              fontSize: '12px', 
              width: '100%'
            }}
          >
            {showSolution ? 'Hide Solution' : 'See Solution'}
          </button>
        </div>
        <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '12px', color: '#333' }}>
          Hints
        </div>
        {hints.length > 0 ? (
          hints.map((hint, index) => (
            <div 
              key={hint.ID || index}
              style={{
                marginBottom: '12px',
                padding: '10px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px'
              }}
            >
              <div style={{ marginBottom: '8px', fontSize: '11px', fontWeight: 'bold', color: '#856404' }}>
                {getScaffoldHintLabel(hint) === 'Text Hint' ? '💡 Text Hint' : `📹 ${getScaffoldHintLabel(hint)}`}
              </div>
              {isCamtasiaScaffold(hint) || (hint.FilePath && hint.FileType) ? (
                renderScaffoldBlock(hint, {
                  buttonStyle: {
                    display: 'inline-block',
                    padding: '4px 8px',
                    backgroundColor: '#007bff',
                    color: '#fff',
                    borderRadius: '4px',
                    border: 'none',
                    fontSize: '11px',
                    cursor: 'pointer'
                  },
                  showInlineVideo: true
                })
              ) : (
                <div
                  style={{ fontSize: '11px', color: '#333', lineHeight: '1.4' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(hint.Desc || '') }}
                />
              )}
            </div>
          ))
        ) : (
          <div style={{ color: '#999', fontStyle: 'italic', fontSize: '11px' }}>
            Empty
          </div>
        )}
      </div>
      {renderPreviewModal()}
    </div>
  );
};

export default ReviewQuestionDisplay;

